/**
 * 商品詳細の下半分（sections/c-product-extra.liquid）
 *
 * <c-product-more> — 「もっとみる」。中身が区切りの高さより長いときだけボタンを出す
 * [data-modal-open] — POINT・注意事項カードを押すと、カード内の <template> をポップアップに差し込んで開く
 */
if (!customElements.get('c-product-more')) {
  customElements.define(
    'c-product-more',
    class CProductMore extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector('[data-more-body]');
        this.button = this.querySelector('[data-more-button]');
        this.label = this.querySelector('[data-more-label]');
        if (!this.body || !this.button) return;

        // 画像の読み込みで高さが変わるので、読み込み後にも判定し直す
        this.check = this.check.bind(this);
        requestAnimationFrame(this.check);
        window.addEventListener('load', this.check, { once: true });

        this.button.addEventListener('click', () => {
          const open = !this.hasAttribute('open');
          this.toggleAttribute('open', open);
          this.button.setAttribute('aria-expanded', String(open));
          this.label.textContent = open ? '閉じる' : 'もっとみる';
        });
      }

      check() {
        if (this.hasAttribute('open')) return;
        const overflow = this.body.scrollHeight > this.body.clientHeight + 4;
        this.button.hidden = !overflow;
        this.toggleAttribute('data-overflow', overflow);
      }
    }
  );
}

(() => {
  const init = (root) => {
    const dialog = root.querySelector('[data-product-modal]');
    if (!dialog || dialog.dataset.ready) return;
    dialog.dataset.ready = '1';
    const body = dialog.querySelector('[data-modal-body]');
    let opener = null;

    root.addEventListener('click', (e) => {
      const button = e.target.closest('[data-modal-open]');
      if (!button) return;
      const tpl = button.parentElement.querySelector('template[data-modal-content]');
      if (!tpl) return;
      body.replaceChildren(tpl.content.cloneNode(true));
      opener = button;
      dialog.showModal();
    });

    dialog.querySelector('[data-modal-close]')?.addEventListener('click', () => dialog.close());
    // 背景（dialog の外側）を押したら閉じる
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => opener?.focus());
  };

  // 本文に貼られた iframe（YouTube など）の縦横比を、width / height 属性から決める（CSS で幅を枠に収めるため）。
  // Shorts のような縦長でも歪まない
  const fitEmbeds = (root) => {
    root.querySelectorAll('iframe[width][height]').forEach((el) => {
      const w = parseFloat(el.getAttribute('width'));
      const h = parseFloat(el.getAttribute('height'));
      if (w > 0 && h > 0) el.style.aspectRatio = `${w} / ${h}`;
    });
  };

  const run = () =>
    document.querySelectorAll('.c-product-extra').forEach((root) => {
      init(root);
      fitEmbeds(root);
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();

/**
 * <c-product-tabs> — スペックの切り替え
 *   PC（750px 以上）：タブ（WAI-ARIA のタブの作法：クリック／←→キーで切り替え）
 *   SP（750px 未満）：アコーディオン（新カンプ 2115:3934）。見出しを押すとその中身だけ開閉・最初は全部閉じる
 *   見出しと中身の並びは CSS（order）で入れ替えているだけで、DOM は同じ
 */
if (!customElements.get('c-product-tabs')) {
  customElements.define(
    'c-product-tabs',
    class CProductTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"], [data-accordion-head]')];
        this.mql = window.matchMedia('(max-width: 749px)');
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => (this.mql.matches ? this.toggle(i) : this.select(i)));
          tab.addEventListener('keydown', (e) => {
            if (this.mql.matches) return; // アコーディオンは標準のボタン操作（Enter／Space）
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            e.preventDefault();
            const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          });
        });
        this.mql.addEventListener('change', () => this.applyMode());
        this.applyMode();
        // 初期状態を決めたあとで開閉アニメーションを有効にする（初期状態は閉じ＝0fr のままなので、読み込み時には動かない）
        this.classList.add('is-ready');
      }

      applyMode() {
        const sp = this.mql.matches;
        this.tabs.forEach((tab) => {
          tab.toggleAttribute('data-accordion-head', true);
          if (sp) {
            tab.setAttribute('role', 'button');
            tab.removeAttribute('aria-selected');
            tab.tabIndex = 0;
          } else {
            tab.setAttribute('role', 'tab');
            tab.removeAttribute('aria-expanded');
          }
        });
        if (sp) this.tabs.forEach((_, i) => this.setOpen(i, false));
        else this.select(0);
      }

      setOpen(index, open) {
        const tab = this.tabs[index];
        tab.setAttribute('aria-expanded', String(open));
        const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
        if (!panel) return;
        panel.hidden = !open;
        panel.setAttribute('role', 'region');
        if (open) panel.querySelector('c-product-more')?.check?.();
      }

      toggle(index) {
        this.setOpen(index, this.tabs[index].getAttribute('aria-expanded') !== 'true');
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', String(on));
          tab.tabIndex = on ? 0 : -1;
          const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
          if (panel) {
            panel.hidden = !on;
            panel.setAttribute('role', 'tabpanel');
          }
          // 隠れていたパネルは高さが測れていないので、表示した時点で「もっとみる」を判定し直す
          if (on) panel?.querySelector('c-product-more')?.check?.();
        });
      }
    }
  );
}
